import {
  DashboardDateRangePreset,
  type DashboardDataset,
  type DashboardParameter,
} from "@t3tools/contracts";
import { Schema } from "effect";

import type { DashboardArtifactOptionsResponse } from "./artifactTypes";

export type ArtifactDateRange = { readonly start: string; readonly end: string };
/** Presets stay presets so a shared "last 30 days" link keeps rolling forward. */
export type ArtifactDateRangeValue =
  | { readonly preset: DashboardDateRangePreset }
  | ArtifactDateRange;
export type ArtifactParameterValue = ArtifactDateRangeValue | string | ReadonlyArray<string>;
export type ArtifactParameterValues = Readonly<Record<string, ArtifactParameterValue>>;

/** Matches the control plane, which resolves presets in this zone. */
export const ARTIFACT_TIME_ZONE = "America/New_York";
/** Keeps parameter ids from colliding with route-owned search params. */
export const ARTIFACT_PARAMETER_SEARCH_PREFIX = "p.";
const DATE_RANGE_SEPARATOR = "..";
const MAX_VALUE_LENGTH = 200;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const DATE_RANGE_PRESET_LABELS: Record<DashboardDateRangePreset, string> = {
  last_7_days: "Last 7 days",
  last_30_days: "Last 30 days",
  last_90_days: "Last 90 days",
  month_to_date: "Month to date",
  previous_month: "Previous month",
  quarter_to_date: "Quarter to date",
  year_to_date: "Year to date",
};

const isDateRangePreset = Schema.is(DashboardDateRangePreset);

function isIsoDate(value: unknown): value is string {
  return (
    typeof value === "string" &&
    ISO_DATE.test(value) &&
    new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value
  );
}

function shiftDays(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function daysInclusive(range: ArtifactDateRange): number {
  return (
    (Date.parse(`${range.end}T00:00:00Z`) - Date.parse(`${range.start}T00:00:00Z`)) / 86_400_000 + 1
  );
}

export function resolveArtifactDateRange(
  value: ArtifactDateRangeValue,
  now: Date = new Date(),
): ArtifactDateRange {
  if (!("preset" in value)) return { start: value.start, end: value.end };
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: ARTIFACT_TIME_ZONE }).format(now);
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  const monthStart = `${today.slice(0, 7)}-01`;
  switch (value.preset) {
    case "last_7_days":
      return { start: shiftDays(today, -6), end: today };
    case "last_30_days":
      return { start: shiftDays(today, -29), end: today };
    case "last_90_days":
      return { start: shiftDays(today, -89), end: today };
    case "month_to_date":
      return { start: monthStart, end: today };
    case "previous_month": {
      const end = shiftDays(monthStart, -1);
      return { start: `${end.slice(0, 7)}-01`, end };
    }
    case "quarter_to_date": {
      const quarterMonth = Math.floor((month - 1) / 3) * 3 + 1;
      return { start: `${year}-${String(quarterMonth).padStart(2, "0")}-01`, end: today };
    }
    case "year_to_date":
      return { start: `${year}-01-01`, end: today };
  }
}

function staticOptionValues(parameter: DashboardParameter): ReadonlySet<string> | null {
  if (parameter.kind === "date_range" || parameter.options.from !== "static") return null;
  return new Set(parameter.options.options.map((option) => option.value));
}

function isAllowedText(value: unknown, allowed: ReadonlySet<string> | null): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= MAX_VALUE_LENGTH &&
    (!allowed || allowed.has(value))
  );
}

/** Returns `value` when it is valid for `parameter`, otherwise `undefined`. */
export function validateArtifactParameterValue(
  parameter: DashboardParameter,
  value: unknown,
): ArtifactParameterValue | undefined {
  if (parameter.kind === "date_range") {
    if (!value || typeof value !== "object") return undefined;
    const record = value as Record<string, unknown>;
    if ("preset" in record) {
      return isDateRangePreset(record.preset) ? { preset: record.preset } : undefined;
    }
    if (!isIsoDate(record.start) || !isIsoDate(record.end) || record.start > record.end) {
      return undefined;
    }
    const range = { start: record.start, end: record.end };
    if (parameter.maxDays !== undefined && daysInclusive(range) > parameter.maxDays) {
      return undefined;
    }
    return range;
  }
  const allowed = staticOptionValues(parameter);
  if (parameter.kind === "select") return isAllowedText(value, allowed) ? value : undefined;
  if (!Array.isArray(value) || !value.every((item) => isAllowedText(item, allowed))) {
    return undefined;
  }
  return [...new Set(value as string[])];
}

export function defaultArtifactParameterValue(
  parameter: DashboardParameter,
): ArtifactParameterValue {
  return parameter.default;
}

export function defaultArtifactParameterValues(
  parameters: ReadonlyArray<DashboardParameter>,
): ArtifactParameterValues {
  return Object.fromEntries(
    parameters.map((parameter) => [parameter.id, defaultArtifactParameterValue(parameter)]),
  );
}

const RANGE_DATE = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
  year: "numeric",
});

/** "Last 30 days" or "Sep 1 – 30, 2026" for the first date filter's default. */
export function artifactDefaultPeriodLabel(
  parameters: ReadonlyArray<DashboardParameter>,
): string | null {
  const parameter = parameters.find((candidate) => candidate.kind === "date_range");
  if (!parameter || parameter.kind !== "date_range") return null;
  const value = parameter.default;
  if ("preset" in value) return DATE_RANGE_PRESET_LABELS[value.preset];
  return RANGE_DATE.formatRange(
    new Date(`${value.start}T00:00:00Z`),
    new Date(`${value.end}T00:00:00Z`),
  );
}

export type ArtifactSelectOption = { label: string; value: string };

type SelectParameter = Exclude<DashboardParameter, { kind: "date_range" }>;

/** Static options, or the rows of the parameter's options dataset once loaded. */
export function artifactParameterOptions(
  parameter: SelectParameter,
  optionsData: Pick<DashboardArtifactOptionsResponse, "rows"> | undefined,
): ArtifactSelectOption[] {
  const { options } = parameter;
  if (options.from === "static") return [...options.options];
  const seen = new Set<string>();
  const result: ArtifactSelectOption[] = [];
  for (const row of optionsData?.rows ?? []) {
    const value = row[options.valueColumn];
    if (value === null || value === undefined || value === "") continue;
    const key = String(value);
    if (seen.has(key)) continue;
    seen.add(key);
    const label = options.labelColumn ? row[options.labelColumn] : undefined;
    result.push({ value: key, label: label == null || label === "" ? key : String(label) });
  }
  return result;
}

/**
 * The options dataset of a select whose choices depend on other selects, such
 * as accounts scoped by rep; `null` for static or date-only options.
 */
export function scopedOptionsDataset(
  parameter: DashboardParameter,
  datasets: ReadonlyArray<Pick<DashboardDataset, "id" | "bind">>,
): string | null {
  if (parameter.kind === "date_range" || parameter.options.from !== "dataset") return null;
  const datasetId = parameter.options.dataset;
  const dataset = datasets.find((candidate) => candidate.id === datasetId);
  return dataset?.bind.some((ref) => !ref.includes(".")) ? datasetId : null;
}

export type ArtifactOptionsState = {
  /** Rows of each options dataset; during a refetch, the previous filters' rows. */
  data: ReadonlyMap<string, Pick<DashboardArtifactOptionsResponse, "rows"> | undefined>;
  /** Options datasets still loading rows for the current filters. */
  loading: ReadonlySet<string>;
};

/**
 * Keeps each scoped select inside its current options. A value the options no
 * longer offer falls back to the default ("All accounts in selection"), and a
 * multi-select drops it. `pending` is true while a non-default value waits for
 * its options to load, so the dashboard isn't queried with a stale scope.
 * A failed options load keeps the value as it is.
 */
export function reconcileArtifactOptionValues(
  parameters: ReadonlyArray<DashboardParameter>,
  datasets: ReadonlyArray<Pick<DashboardDataset, "id" | "bind">>,
  values: ArtifactParameterValues,
  options: ArtifactOptionsState,
): { values: ArtifactParameterValues; changed: boolean; pending: boolean } {
  let changed = false;
  let pending = false;
  const next: Record<string, ArtifactParameterValue> = { ...values };
  for (const parameter of parameters) {
    const datasetId = scopedOptionsDataset(parameter, datasets);
    if (datasetId === null || parameter.kind === "date_range") continue;
    const value = values[parameter.id];
    const defaults: ReadonlyArray<string> =
      parameter.kind === "select" ? [parameter.default] : parameter.default;
    const items = typeof value === "string" ? [value] : Array.isArray(value) ? value : [];
    if (items.every((item) => defaults.includes(item))) continue;
    if (options.loading.has(datasetId)) {
      pending = true;
      continue;
    }
    const rows = options.data.get(datasetId);
    if (!rows) continue;
    const allowed = new Set([
      ...artifactParameterOptions(parameter, rows).map((option) => option.value),
      ...defaults,
    ]);
    const kept = items.filter((item) => allowed.has(item));
    if (kept.length === items.length) continue;
    next[parameter.id] = parameter.kind === "select" ? parameter.default : kept;
    changed = true;
  }
  return { values: changed ? next : values, changed, pending };
}

/** Fills missing or invalid values with each parameter's default. */
export function normalizeArtifactParameterValues(
  parameters: ReadonlyArray<DashboardParameter>,
  values: Readonly<Record<string, unknown>>,
): ArtifactParameterValues {
  return Object.fromEntries(
    parameters.map((parameter) => [
      parameter.id,
      validateArtifactParameterValue(parameter, values[parameter.id]) ??
        defaultArtifactParameterValue(parameter),
    ]),
  );
}

function searchKey(parameter: DashboardParameter): string {
  return `${ARTIFACT_PARAMETER_SEARCH_PREFIX}${parameter.id}`;
}

/**
 * Writes every parameter as `p.<id>`: a date range as a preset or
 * `start..end`, a select as its value, and a multi-select as repeated keys
 * (one empty key for an empty selection). Other search params are kept.
 */
export function encodeArtifactParameterSearch(
  parameters: ReadonlyArray<DashboardParameter>,
  values: ArtifactParameterValues,
  base?: URLSearchParams,
): URLSearchParams {
  const search = new URLSearchParams(base);
  const normalized = normalizeArtifactParameterValues(parameters, values);
  for (const parameter of parameters) {
    const key = searchKey(parameter);
    const value = normalized[parameter.id];
    search.delete(key);
    if (parameter.kind === "date_range") {
      const range = value as ArtifactDateRangeValue;
      search.set(
        key,
        "preset" in range ? range.preset : `${range.start}${DATE_RANGE_SEPARATOR}${range.end}`,
      );
    } else if (parameter.kind === "select") {
      search.set(key, value as string);
    } else {
      const list = value as ReadonlyArray<string>;
      if (list.length === 0) search.append(key, "");
      for (const item of list) search.append(key, item);
    }
  }
  return search;
}

function readSearchValue(parameter: DashboardParameter, search: URLSearchParams): unknown {
  const key = searchKey(parameter);
  if (!search.has(key)) return undefined;
  if (parameter.kind === "multi_select") {
    const all = search.getAll(key);
    return all.length === 1 && all[0] === "" ? [] : all;
  }
  const raw = search.get(key) ?? "";
  if (parameter.kind === "select") return raw;
  if (isDateRangePreset(raw)) return { preset: raw };
  const [start, end, ...rest] = raw.split(DATE_RANGE_SEPARATOR);
  return rest.length === 0 ? { start, end } : undefined;
}

/** Reads `p.<id>` values, falling back to defaults for missing or invalid input. */
export function decodeArtifactParameterSearch(
  parameters: ReadonlyArray<DashboardParameter>,
  search: URLSearchParams | string,
): ArtifactParameterValues {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  return normalizeArtifactParameterValues(
    parameters,
    Object.fromEntries(
      parameters.map((parameter) => [parameter.id, readSearchValue(parameter, params)]),
    ),
  );
}

/**
 * Request parameters with date ranges resolved to dates. With `only`, other
 * parameters stay out so a change to them does not change the request.
 */
export function artifactRequestParameters(
  parameters: ReadonlyArray<DashboardParameter>,
  values: ArtifactParameterValues,
  now: Date = new Date(),
  only?: ReadonlySet<string>,
): Record<string, ArtifactDateRange | string | ReadonlyArray<string>> {
  const normalized = normalizeArtifactParameterValues(parameters, values);
  const request: Record<string, ArtifactDateRange | string | ReadonlyArray<string>> = {};
  for (const parameter of parameters) {
    if (only && !only.has(parameter.id)) continue;
    const value = normalized[parameter.id]!;
    request[parameter.id] =
      parameter.kind === "date_range"
        ? resolveArtifactDateRange(value as ArtifactDateRangeValue, now)
        : (value as string | ReadonlyArray<string>);
  }
  return request;
}

/** Request parameters for one dataset: only the parameters it binds. */
export function artifactDatasetRequestParameters(
  parameters: ReadonlyArray<DashboardParameter>,
  dataset: Pick<DashboardDataset, "bind">,
  values: ArtifactParameterValues,
  now: Date = new Date(),
): Record<string, ArtifactDateRange | string | ReadonlyArray<string>> {
  const bound = new Set(dataset.bind.map((ref) => ref.split(".")[0]!));
  return artifactRequestParameters(parameters, values, now, bound);
}
